<template>
  <div class="codegen-page space-y-5">
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">QR Code Generator</h1>
      <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Generate customer and loading area QR codes</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Form -->
      <div class="lg:col-span-1">
        <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
            <h2 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Select Supplier</h2>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <label for="cg-supplier" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Supplier</label>
              <select
                id="cg-supplier"
                v-model="selectedSupplierId"
                class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="" disabled>Select a supplier</option>
                <option :value="supplier.id" v-for="supplier in suppliers" :key="supplier.id">
                  {{ supplier.suppliername }}
                </option>
              </select>
            </div>
            <div v-if="selectedSupplierId && locations.length">
              <label for="cg-location" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Location</label>
              <select
                id="cg-location"
                v-model="selectedLocationId"
                class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="" disabled>Select a location</option>
                <option :value="location.locationid" v-for="location in locations" :key="location.locationid">
                  {{ location.locationname }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- QR Codes -->
      <div class="lg:col-span-2">
        <div v-if="value" class="space-y-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Customer Code -->
            <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 text-center">
              <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">Customer Code</p>
              <div class="flex justify-center mb-4">
                <QrcodeVue :value="value" :size="qrSize" level="L" class="bg-white p-3 rounded-lg" />
              </div>
            </div>

            <!-- Loading Area Code -->
            <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 text-center">
              <p class="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">Loading Area Code</p>
              <div class="flex justify-center mb-4">
                <QrcodeVue :value="loadingAreaValue" :size="qrSize" level="L" class="bg-white p-3 rounded-lg" />
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-wrap gap-2">
            <a :href="value" target="_blank"
              class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-secondary-600 text-white hover:bg-secondary-700 transition-all duration-200">
              <span class="material-icons text-[18px]" aria-hidden="true">open_in_new</span>
              View Customer Page
            </a>
            <button
              @click="createOTUCode"
              :disabled="isCreating"
              class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <span class="material-icons text-[18px]" :class="{ 'animate-spin': isCreating }" aria-hidden="true">{{ isCreating ? 'autorenew' : 'qr_code' }}</span>
              {{ isCreating ? 'Creating...' : 'Create OTU Code' }}
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="bg-neutral-50 dark:bg-neutral-800 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700 flex flex-col items-center justify-center py-16">
          <span class="material-icons text-4xl text-neutral-300 dark:text-neutral-600 mb-3" aria-hidden="true">qr_code_2</span>
          <p class="text-neutral-500 dark:text-neutral-400 font-medium">Select a supplier to generate QR codes</p>
        </div>
      </div>
    </div>

    <!-- Active Codes Table -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      <div class="px-6 py-4 border-b border-neutral-100 dark:border-neutral-700">
        <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Generated Codes</h2>
      </div>
      <DataTable
        :data="codes"
        :columns="tableColumns"
        :loading="codesLoading"
        :searchable="false"
        :show-index="false"
        :pagination="true"
        loading-text="Loading codes..."
        empty-text="No active codes found"
      >
        <template #cell-supplierName="{ row }">
          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ row.supplierName }}</span>
        </template>

        <template #cell-createdAt="{ row }">
          <span v-if="row.createdAt" class="text-sm text-neutral-700 dark:text-neutral-300">
            {{ row.createdAt.toDate().toDateString() }}
          </span>
        </template>

        <template #actions="{ row }">
          <button
            @click="confirmDeleteCode(row)"
            class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            title="Delete code"
            aria-label="Delete code"
          >
            <span class="material-icons text-[16px]" aria-hidden="true">delete_outline</span>
          </button>
        </template>
      </DataTable>
    </div>

    <!-- Delete Confirm -->
    <ConfirmDialog
      :show="showDeleteConfirm"
      title="Delete Code"
      :message="`Delete the OTU code for ${codeToDelete?.supplierName || 'this supplier'}${codeToDelete?.locationName ? ' — ' + codeToDelete.locationName : ''}? This cannot be undone.`"
      confirm-label="Delete"
      :loading="isDeleting"
      danger
      @confirm="deleteRecord"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script>
import useCollection from "@/composables/useCollection";
import useToast from "@/composables/useToast";
import DataTable from "@/components/ui/DataTable.vue";
import ConfirmDialog from "@/components/ui/ConfirmDialog.vue";
import {
  collection, deleteDoc, doc, onSnapshot, Timestamp, query, orderBy,
} from "@firebase/firestore";
import QrcodeVue from "qrcode.vue";
import { ref, computed, watch, watchEffect } from "vue";
import { db } from "@/firebase/config";
import { useStore } from "vuex";

export default {
  components: { QrcodeVue, DataTable, ConfirmDialog },
  setup() {
    const store = useStore();
    store.commit("setCurrentRoute", "CodeGenerator");
    const { showToast } = useToast();

    const suppliers = ref(null);
    const suppliersLoaded = ref(false);
    let colRefSup = collection(db, "suppliers");
    colRefSup = query(colRefSup, orderBy("suppliername"));

    const unSub2 = onSnapshot(colRefSup, (snapshot) => {
      let results = [];
      snapshot.docs.forEach((doc) => {
        if (doc.data().siteid == store.state.currentSite) {
          results.push({ ...doc.data(), id: doc.id });
        }
      });
      suppliers.value = results;
      suppliersLoaded.value = true;
    });

    const { addDoucment } = useCollection("generatedCodes");
    const allCodes = ref([]);
    const codesLoaded = ref(false);
    let colRef = collection(db, "generatedCodes");
    const unsub = onSnapshot(colRef, (snapshot) => {
      let results = [];
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id });
      });
      allCodes.value = results;
      codesLoaded.value = true;
    });

    // Site-scoped, newest-first list. Legacy docs predate the siteId field, so
    // they are matched through the current site's supplier list instead.
    // Filtering/sorting stays client-side to avoid a composite-index requirement.
    const codes = computed(() => {
      const siteSupplierIds = new Set((suppliers.value || []).map((s) => s.id));
      return allCodes.value
        .filter((c) => (c.siteId ? c.siteId === store.state.currentSite : siteSupplierIds.has(c.supplierId)))
        .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
    });

    const codesLoading = computed(() => !codesLoaded.value || !suppliersLoaded.value);

    const tableColumns = [
      { key: "id", label: "ID", sortable: true, align: "center" },
      { key: "supplierName", label: "Supplier", sortable: true },
      { key: "locationName", label: "Location", sortable: true, align: "center" },
      { key: "createdAt", label: "Created", sortable: true, align: "center" },
    ];

    const url = "https://merchant.neutralfuels.com";
    const value = ref(null);
    const loadingAreaValue = ref(null);
    const qrSize = 260;

    const selectedSupplierId = ref("");
    const selectedLocationId = ref("");
    const selectedLocation = ref(null);
    const selectedSupplier = ref(null);
    const locations = ref(null);

    watch(selectedSupplierId, () => {
      value.value = null;
      loadingAreaValue.value = null;
      selectedSupplier.value = null;
      selectedLocationId.value = "";
      selectedLocation.value = null;
      locations.value = null;
      suppliers.value.forEach((supplier) => {
        if (supplier.id == selectedSupplierId.value) {
          selectedSupplier.value = { ...supplier };
          locations.value = supplier.locations;
        }
      });
      if (!locations.value.length) {
        selectedLocation.value = {};
        value.value = `${url}?supplierid=${selectedSupplierId.value}&locationid=0`;
        loadingAreaValue.value = `?supplierid=${selectedSupplierId.value}&locationid=0`;
      }
    });

    watch(selectedLocationId, () => {
      locations.value.forEach((location) => {
        if (location.locationid == selectedLocationId.value) {
          selectedLocation.value = { ...location };
          value.value = `${url}?supplierid=${selectedSupplierId.value}&locationid=${selectedLocationId.value}`;
          loadingAreaValue.value = `?supplierid=${selectedSupplierId.value}&locationid=${selectedLocationId.value}`;
        }
      });
    });

    watchEffect((onInvalidate) => {
      onInvalidate(() => { unsub(); unSub2(); });
    });

    const isCreating = ref(false);
    const createOTUCode = async () => {
      if (isCreating.value) return;
      isCreating.value = true;
      try {
        const dataObject = {
          createdAt: Timestamp.now(),
          siteId: store.state.currentSite,
          supplierId: selectedSupplierId.value,
          locationId: selectedLocationId.value ? selectedLocationId.value : "0",
          supplierName: selectedSupplier.value.suppliername,
          locationName: selectedLocationId.value ? selectedLocation.value.locationname : null,
        };
        // addDoucment catches internally and returns undefined on failure
        const res = await addDoucment(dataObject);
        if (!res) throw new Error("Write failed");
        showToast("success", `OTU code created for ${dataObject.supplierName}`);
      } catch (error) {
        showToast("error", "Failed to create OTU code. Please try again.");
      } finally {
        isCreating.value = false;
      }
    };

    const showDeleteConfirm = ref(false);
    const codeToDelete = ref(null);
    const isDeleting = ref(false);

    const confirmDeleteCode = (row) => {
      codeToDelete.value = row;
      showDeleteConfirm.value = true;
    };

    const deleteRecord = async () => {
      if (!codeToDelete.value) return;
      isDeleting.value = true;
      try {
        await deleteDoc(doc(db, "generatedCodes/" + codeToDelete.value.id));
        showDeleteConfirm.value = false;
        codeToDelete.value = null;
        showToast("success", "Code deleted");
      } catch (error) {
        console.error(error.message);
        showToast("error", "Failed to delete code. Please try again.");
      } finally {
        isDeleting.value = false;
      }
    };

    return {
      value, loadingAreaValue, selectedLocationId, selectedSupplierId,
      suppliers, locations, qrSize, createOTUCode, codes, tableColumns, deleteRecord,
      codesLoading, isCreating, showDeleteConfirm, codeToDelete, isDeleting, confirmDeleteCode,
    };
  },
};
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .codegen-page > * {
    animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;
  }
  .codegen-page > *:nth-child(1) { animation-delay: 0s; }
  .codegen-page > *:nth-child(2) { animation-delay: 0.05s; }
  .codegen-page > *:nth-child(3) { animation-delay: 0.1s; }
}

@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
